<script setup lang="ts">
import { toRef } from 'vue';
import type { AnswersByQuestion, MatrixPlayer, PlayedQuestion } from '../../util/answerRecapMatrix';
import { useAnswerRecapMatrix } from '../../composables/useAnswerRecapMatrix';
import useLocale from '../../../composables/use-locale';
import AnswerRecapMatrixTable from './results/AnswerRecapMatrixTable.vue';
import AnswerRecapMatrixLegend from './results/AnswerRecapMatrixLegend.vue';
import QuestionBreakdownDialog from './QuestionBreakdownDialog.vue';
import PlayerDetailDialog from './PlayerDetailDialog.vue';

const props = defineProps<{
  questions: PlayedQuestion[];
  answersByQuestion: AnswersByQuestion;
  players: MatrixPlayer[];
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const { t } = useLocale();

const { matrix, rows, openQuestionIndex, openPlayerId, openQuestion, openPlayerRow } = useAnswerRecapMatrix({
  questions: toRef(props, 'questions'),
  answersByQuestion: toRef(props, 'answersByQuestion'),
  players: toRef(props, 'players'),
});
</script>

<template>
  <v-card
    class="answer-recap-matrix chalkboard-font d-flex flex-column"
    color="surface"
    rounded="lg"
    elevation="4"
    data-testid="quizter-answer-recap-matrix"
  >
    <div class="flex-0-0 d-flex flex-wrap align-center justify-space-between ga-4 px-4 py-2 bg-surface-container-low">
      <h2 class="chalkduster-font text-h6 ma-0">{{ t('results.answer_recap.title') }}</h2>

      <v-btn
        icon="fa:fawsb fa-xmark"
        variant="text"
        color="on-surface-medium"
        :aria-label="t('results.answer_recap.close')"
        data-testid="quizter-answer-recap-close"
        @click="emit('close')"
      />
    </div>

    <div class="matrix-scroll my-7">
      <AnswerRecapMatrixTable
        :rows="rows"
        :columns="matrix.columns"
        @open-question="openQuestionIndex = $event"
        @open-player="openPlayerId = $event"
      />
    </div>

    <AnswerRecapMatrixLegend class="flex-0-0" :players="matrix.participantCount" :questions="matrix.questionCount" />

    <QuestionBreakdownDialog
      :model-value="openQuestion !== null"
      :column="openQuestion"
      :question-count="matrix.questionCount"
      :answers-by-question="answersByQuestion"
      :players="players"
      @update:model-value="openQuestionIndex = null"
    />

    <PlayerDetailDialog
      :model-value="openPlayerRow !== null"
      :row="openPlayerRow"
      :columns="matrix.columns"
      @update:model-value="openPlayerId = null"
    />
  </v-card>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'vuetify/settings' as settings;

/*
 A frame the table scrolls inside rather than a card that grows with it: cells are a fixed size, so a
 5-question game and a 30-question one present at the same scale instead of each picking their own.
 Sized from the viewport rather than the parent, like Card.vue: the layout centres every view in a
 content-sized flex item, so no ancestor has a width of its own to measure.
 */
.answer-recap-matrix {
  --recap-cell-size: 44px;
  --recap-row-height: 36px;

  // 48px is the layout's pa-6. Phones get the full gutter-to-gutter width; there is nothing to spend
  // on a frame the table would only scroll inside anyway.
  min-width: calc(100vw - 48px);
  max-width: calc(100vw - 48px);
  min-height: 60vh;
  max-height: 80vh;

  @media #{map.get(settings.$display-breakpoints, 'sm-and-up')} {
    min-width: 60vw;
    max-width: 80vw;
  }
}

/*
 The only scroll container. Flex with an auto-margined table rather than align-items: center, which
 would put the first row out of reach once the table outgrows the frame.

 Padding on a scroll container sits inside the scrollport and scrolls with the content, so a pinned
 row or column never covers it and cells slide through the gap. All the breathing room here is
 therefore outside the box: margin above and below, and ps-4/pe-4 on the table's own edge cells.
 */
.matrix-scroll {
  // min-height: 0 is what makes the scroll happen here rather than overflowing the card: a flex item
  // defaults to min-height: auto and would refuse to shrink below the full height of the table.
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  overflow: auto;
}
</style>
